import React, { useEffect, useState, useMemo, useRef } from "react";

// Column dropdown filter — mirrors the one in scenes/CMSAnalysis so both tables
// share the same look & UX. Kept local to this feature to avoid coupling the
// two pages (a future refactor could lift this into a shared component).
//
// Filter value semantics:
//   null      → all selected (no filter applied)
//   []        → none checked (0 rows pass — user unchecked everything)
//   ["A","B"] → only those values pass

const DropdownFilter = ({ columnKey, label, optionsData, selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredValue, setHoveredValue] = useState(null);
  const containerRef = useRef(null);

  const uniqueValues = useMemo(() => {
    const set = new Set(optionsData.map((row) => row[columnKey]).filter((v) => v != null));
    return [...set].sort((a, b) =>
      String(a).localeCompare(String(b), "tr", { sensitivity: "base", numeric: true })
    );
  }, [optionsData, columnKey]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const isAllSelected = selectedValues === null;
  const isFiltered = selectedValues !== null;
  const isChecked = (val) => isAllSelected || selectedValues.includes(val);

  const clearFilter = () => onChange(null);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange(null);
    }
  };

  const toggleValue = (val) => {
    if (isAllSelected) {
      onChange(uniqueValues.filter((v) => v !== val));
    } else if (selectedValues.includes(val)) {
      onChange(selectedValues.filter((v) => v !== val));
    } else {
      const next = [...selectedValues, val];
      onChange(next.length === uniqueValues.length ? null : next);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        onClick={() => setIsOpen((o) => !o)}
        style={{
          padding: "7px 12px",
          fontSize: 13,
          border: `1.5px solid ${isFiltered ? "#2563eb" : "#d1d5db"}`,
          borderRadius: 6,
          backgroundColor: isFiltered ? "#eff6ff" : "#fff",
          color: isFiltered ? "#1d4ed8" : "#374151",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontWeight: isFiltered ? 600 : "normal",
          whiteSpace: "nowrap",
          minWidth: 120,
        }}
      >
        <span style={{ flex: 1, textAlign: "left" }}>
          {label}
          {isFiltered && (
            <span
              style={{
                marginLeft: 6,
                backgroundColor: selectedValues.length === 0 ? "#ef4444" : "#2563eb",
                color: "#fff",
                borderRadius: 10,
                padding: "0 6px",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {selectedValues.length}
            </span>
          )}
        </span>
        <span style={{ opacity: 0.6, fontSize: 11 }}>{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 500,
            backgroundColor: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            minWidth: 240,
            maxHeight: 320,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: "2px solid #e5e7eb",
              backgroundColor: "#f9fafb",
              flexShrink: 0,
              gap: 8,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={toggleSelectAll}
                style={{ width: 15, height: 15, cursor: "pointer" }}
              />
              Tümünü Seç
              <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>
                {uniqueValues.length} öğe
              </span>
            </label>
            {isFiltered && (
              <button
                onClick={clearFilter}
                style={{
                  flexShrink: 0,
                  padding: "3px 10px",
                  fontSize: 12,
                  border: "1px solid #fca5a5",
                  borderRadius: 4,
                  backgroundColor: "#fef2f2",
                  color: "#dc2626",
                  cursor: "pointer",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                Tümünü Kaldır
              </button>
            )}
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {uniqueValues.map((val) => (
              <label
                key={val}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "7px 14px",
                  cursor: "pointer",
                  fontSize: 13,
                  color: "#374151",
                  backgroundColor: hoveredValue === val ? "#f3f4f6" : "",
                }}
                onMouseEnter={() => setHoveredValue(val)}
                onMouseLeave={() => setHoveredValue(null)}
              >
                <input
                  type="checkbox"
                  checked={isChecked(val)}
                  onChange={() => toggleValue(val)}
                  style={{ width: 15, height: 15, cursor: "pointer", flexShrink: 0 }}
                />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {val}
                </span>
                {hoveredValue === val && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange([val]);
                      setIsOpen(false);
                    }}
                    style={{
                      flexShrink: 0,
                      padding: "2px 7px",
                      fontSize: 11,
                      border: "1px solid #2563eb",
                      borderRadius: 4,
                      backgroundColor: "#eff6ff",
                      color: "#1d4ed8",
                      cursor: "pointer",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Sadece Bu
                  </button>
                )}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownFilter;
